import { BIZ_BASE_URL } from '@constants/api.constants';
import { get, put, remove } from './api.service';
import { LocalStorageKey, BooleanString } from '@constants/storage-keys.constants';
import { AxiosResponse } from 'axios';
import { DeviceType } from '@constants/notification.constants';
import { logFirebaseEnablePushNotif } from '@helpers/firebase-helpers';

interface IPayload {
	subscription: {
		endpoint?: string;
		auth?: string;
		p256dh?: string;
	};
	deviceType: DeviceType;
	registrationId?: string;
	renew?: boolean;
}

async function getVapidPublicKey() {
	try {
		const rs = await get(`${BIZ_BASE_URL}/users/vapid-public-key`);
		if (rs) {
			return rs.data;
		}
	} catch (error) {
		console.error('Error getting VAPID public key:', error);
	}
}

async function sendSubscriptionToServer(
	subscription: PushSubscription,
	uid: string,
	renewSubscription: boolean,
	pushNotifEventKey?: { outlet: string; brand: string },
	isFirst?: boolean,
) {
	const subscriptionJSON = subscription.toJSON();
	let payload: IPayload = {
		subscription: {
			endpoint: subscriptionJSON.endpoint,
			auth: subscriptionJSON.keys?.auth,
			p256dh: subscriptionJSON.keys?.p256dh,
		},
		deviceType: DeviceType.BROWSER,
	};
	if (renewSubscription) {
		const registrationId = window.localStorage.getItem(LocalStorageKey.REGISTRATION_ID) || '';
		payload = {
			...payload,
			registrationId,
			renew: true,
		};
	}
	try {
		const rs = (await put(`${BIZ_BASE_URL}/users/${uid}/device`, payload)) as AxiosResponse & { registrationId: string };
		if (rs.registrationId) {
			if (isFirst && pushNotifEventKey) {
				logFirebaseEnablePushNotif(pushNotifEventKey);
			}
			window.localStorage.setItem(LocalStorageKey.REGISTRATION_ID, rs.registrationId);
			window.localStorage.setItem(LocalStorageKey.IS_SUBSCRIBED, BooleanString.TRUE);
		}
		return rs;
	} catch (error) {
		console.error('Error sending subscription to server:', error);
		throw error;
	}
}

async function cancelSubscriptionFromServer(subscription: PushSubscription, uid: string) {
	const subscriptionJSON = subscription.toJSON();
	const payload = {
		data: {
			subscription: {
				endpoint: subscriptionJSON.endpoint,
				auth: subscriptionJSON.keys?.auth,
				p256dh: subscriptionJSON.keys?.p256dh,
			},
			deviceType: DeviceType.BROWSER,
			registrationId: window.localStorage.getItem(LocalStorageKey.REGISTRATION_ID),
		},
	};

	try {
		const response = await remove(`${BIZ_BASE_URL}/users/${uid}/device`, payload);
		window.localStorage.removeItem(LocalStorageKey.REGISTRATION_ID);
		window.localStorage.setItem(LocalStorageKey.IS_SUBSCRIBED, BooleanString.FALSE);
		return response;
	} catch (error) {
		console.error('Error canceling subscription from server:', error);
		throw error;
	}
}

export async function unsubscribe(uid: string) {
	try {
		const registration = await navigator.serviceWorker.ready;
		if (!registration) {
			console.error('No service worker registration');
			return;
		}
		const subscription = await registration.pushManager.getSubscription();
		if (subscription) {
			await subscription.unsubscribe();
			const response = await cancelSubscriptionFromServer(subscription, uid);
			return response;
		}
	} catch (error) {
		console.error('Unsubscription error:', error);
	}
}

export async function subscribe(uid: string, renewSubscription = false, isFirst = false, pushNotifEventKey?: { outlet: string; brand: string }) {
	try {
		const registration = await navigator.serviceWorker.getRegistration();
		if (!registration) {
			console.error('No service worker registration');
			return;
		}
		const readyRegistration = await navigator.serviceWorker.ready;
		const vapidPublicKey = await getVapidPublicKey();
		const subscription = await readyRegistration.pushManager.subscribe({
			userVisibleOnly: true,
			applicationServerKey: vapidPublicKey,
		});
		const response = await sendSubscriptionToServer(subscription, uid, renewSubscription, pushNotifEventKey, isFirst);
		return response;
	} catch (error) {
		console.error('Unable to subscribe to push:', error);
	}
}
